import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';
import { getDataCompletenessAnalysis, uploadCCBProfileImageV2 } from '../firebase/functionsService';
import { getGuestCheckInHistory, markAllGuestNotesAsRead, getAllGuestsUnreadCounts, updateParticipant, uploadGuestPhoto } from '../firebase/firestoreService';
import { resizeImage } from '../utils/imageUtils';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import AddNoteModal from '../components/AddNoteModal';
import EditGuestModal from '../components/EditGuestModal';
import { useAppBusy } from '../utils/appUpdate';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/GuestProfiles.css';
import { readOnlyProps } from '../utils/viewAs';

// Unread counts cover every check-in note, including notes on inactive guests
// and volunteers, who aren't listed here. Keep only guests on this page so the
// bell count matches what can actually be opened and read.
function countsForListedGuests(counts, guests) {
  const listedIds = new Set(guests.map(g => g.id));
  return Object.fromEntries(
    Object.entries(counts).filter(([guestId]) => listedIds.has(guestId))
  );
}

function GuestProfiles() {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'complete', 'incomplete'
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [selectedGuestForNote, setSelectedGuestForNote] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedGuestForEdit, setSelectedGuestForEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [guestNotes, setGuestNotes] = useState({});
  const [unreadNotesCounts, setUnreadNotesCounts] = useState({});
  const [viewedInSession, setViewedInSession] = useState(new Set()); // Track guests viewed this session
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null); // Track which guest's photo is uploading to CCB
  useAppBusy('guest-profiles-photo', uploadingPhotoFor !== null);
  const [photoUploadMessage, setPhotoUploadMessage] = useState(null); // {guestId, type, text}
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100 progress percentage
  const [uploadStage, setUploadStage] = useState(''); // Current stage description
  const [placeholderImages, setPlaceholderImages] = useState(new Set()); // Track guest IDs with placeholder images (600x400)
  const [failedImages, setFailedImages] = useState(new Set()); // Track guest IDs whose images failed to load
  const shouldResetPage = useRef(false); // Track when page should reset to 1
  const fileInputRef = useRef(null);
  const [pendingPhotoGuest, setPendingPhotoGuest] = useState(null);
  const currentUser = getCurrentUser();

  // Check if a guest has a real image (not a CCB placeholder or failed load)
  const hasRealImage = (guest) => {
    return guest.image && !placeholderImages.has(guest.id) && !failedImages.has(guest.id);
  };

  // Check if a guest's photo needs attention (missing real photo or needs CCB upload)
  const needsPhotoUpload = (guest) => {
    if (!guest.id.startsWith('ccb_')) return false;
    // Placeholder image detected
    if (placeholderImages.has(guest.id)) return true;
    // No image at all
    if (!guest.image) return true;
    // App-uploaded image not yet pushed to CCB
    if (guest.imageSource === 'app' && !guest.ccbImageUploaded) return true;
    return false;
  };

  // Called when an avatar image loads — detects CCB 600x400 placeholders
  const handleAvatarLoad = (guestId, event) => {
    const img = event.target;
    if (img.naturalWidth === 600 && img.naturalHeight === 400) {
      setPlaceholderImages(prev => new Set(prev).add(guestId));
    }
  };

  // Called when an avatar image fails to load
  const handleAvatarError = (guestId) => {
    setFailedImages(prev => new Set(prev).add(guestId));
  };

  // Load user role on mount
  useEffect(() => {
    const loadUserRole = async () => {
      if (currentUser) {
        const role = await getUserRole(currentUser.uid);
        setUserRole(role);
      }
    };
    loadUserRole();
  }, [currentUser]);

  // Watch for URL parameter changes
  useEffect(() => {
    const unreadParam = searchParams.get('unread');
    if (unreadParam === 'true') {
      setShowUnreadOnly(true);
    } else {
      // Clear viewed session when leaving unread-only view
      setViewedInSession(new Set());
    }
  }, [searchParams]);

  useEffect(() => {
    // Set up real-time Firestore listener
    const db = getFirestore();
    const participantsRef = collection(db, 'participants');
    const q = query(participantsRef, where('groupId', '==', '1269'));

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        try {
          // Get all participants from snapshot. Inactive (CCB-deactivated)
          // guests are hidden — CCB is the source of truth for status.
          const allParticipants = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(p => p.inactive !== true);

          // Get completeness analysis
          const analysisResult = await getDataCompletenessAnalysis('1269');

          // Create a map of incomplete profiles for quick lookup
          const incompleteMap = new Map();
          if (analysisResult.incompleteProfiles) {
            analysisResult.incompleteProfiles.forEach(profile => {
              incompleteMap.set(profile.id, profile);
            });
          }

          // Define fields to check for completeness calculation
          const fieldsToCheck = [
            'firstName', 'lastName', 'gender', 'birthday',
            'phone', 'mobilePhone', 'homePhone', 'email',
            'street', 'city', 'state', 'zip',
            'emergencyContactName', 'emergencyContactPhone',
            'image'
          ];

          // Process all participants
          const processedGuests = allParticipants.map(participant => {
            const incompleteData = incompleteMap.get(participant.id);

            if (incompleteData) {
              // Use data from incomplete profiles analysis
              return {
                ...participant,
                ...incompleteData,
                isComplete: false
              };
            } else {
              // Calculate completeness for complete profiles
              let filledFields = 0;
              fieldsToCheck.forEach(field => {
                if (participant[field] && participant[field].toString().trim() !== '') {
                  filledFields++;
                }
              });

              const completeness = Math.round((filledFields / fieldsToCheck.length) * 100);

              return {
                ...participant,
                completeness,
                missingFields: [],
                criticalMissing: false,
                emergencyContactMissing: !participant.emergencyContactName || !participant.emergencyContactPhone,
                isComplete: completeness === 100
              };
            }
          });

          setGuests(processedGuests);
          setLoading(false);
        } catch (err) {
          console.error('Error processing guests:', err);
          setError(err.message || 'Failed to process guest profiles');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error listening to participants:', err);
        setError(err.message || 'Failed to load guest profiles');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  // When a guest's image URL changes (new upload, sync update, removal),
  // clear any stale 'failed' or 'placeholder' marker for them so the new
  // URL gets a fresh load attempt — otherwise hasRealImage() keeps
  // returning false and the avatar stays as a placeholder until a manual
  // page refresh.
  const prevImageUrlsRef = useRef(new Map());
  useEffect(() => {
    const prev = prevImageUrlsRef.current;
    const next = new Map();
    const changedIds = [];

    guests.forEach(g => {
      const url = g.image || '';
      next.set(g.id, url);
      if (prev.has(g.id) && prev.get(g.id) !== url) {
        changedIds.push(g.id);
      }
    });

    if (changedIds.length > 0) {
      setFailedImages(prev => {
        const updated = new Set(prev);
        changedIds.forEach(id => updated.delete(id));
        return updated;
      });
      setPlaceholderImages(prev => {
        const updated = new Set(prev);
        changedIds.forEach(id => updated.delete(id));
        return updated;
      });
    }

    prevImageUrlsRef.current = next;
  }, [guests]);

  // Recalculate completeness when placeholder images are detected
  useEffect(() => {
    if (placeholderImages.size === 0) return;

    const fieldsToCheck = [
      'firstName', 'lastName', 'gender', 'birthday',
      'phone', 'mobilePhone', 'homePhone', 'email',
      'street', 'city', 'state', 'zip',
      'emergencyContactName', 'emergencyContactPhone',
      'image'
    ];

    setGuests(prev => prev.map(guest => {
      if (!placeholderImages.has(guest.id)) return guest;

      // Recalculate with image treated as missing
      let filledFields = 0;
      fieldsToCheck.forEach(field => {
        if (field === 'image') return; // placeholder = missing
        if (guest[field] && guest[field].toString().trim() !== '') {
          filledFields++;
        }
      });

      const completeness = Math.round((filledFields / fieldsToCheck.length) * 100);
      return {
        ...guest,
        completeness,
        isComplete: completeness === 100
      };
    }));
  }, [placeholderImages]);

  useEffect(() => {
    filterGuests();
    if (shouldResetPage.current) {
      setCurrentPage(1);
      shouldResetPage.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterStatus, showUnreadOnly, guests, unreadNotesCounts, viewedInSession]);

  // Load unread notes counts for all guests - OPTIMIZED (2 queries instead of 400)
  useEffect(() => {
    if (!currentUser || guests.length === 0) return;

    const loadUnreadCounts = async () => {
      try {
        const counts = await getAllGuestsUnreadCounts(currentUser.uid);
        setUnreadNotesCounts(countsForListedGuests(counts, guests));
      } catch (err) {
        console.error('Error loading unread counts:', err);
        setUnreadNotesCounts({});
      }
    };

    loadUnreadCounts();
  }, [guests, currentUser]);

  // Subscribe to both noteReads AND checkIns changes to update counts in real-time
  useEffect(() => {
    if (!currentUser) return;

    const db = getFirestore();

    // Subscribe to noteReads changes (when notes are marked as read)
    const noteReadsRef = collection(db, 'noteReads');
    const noteReadsQuery = query(noteReadsRef, where('userId', '==', currentUser.uid));

    // Subscribe to checkIns changes (when new notes are added - check-in or check-out notes)
    const checkInsRef = collection(db, 'checkIns');
    const checkInsQuery = query(checkInsRef, where('checkInNotes', '!=', ''));
    const checkOutNotesQuery = query(checkInsRef, where('checkOutNotes', '!=', ''));

    const reloadCounts = async () => {
      try {
        const counts = await getAllGuestsUnreadCounts(currentUser.uid);
        setUnreadNotesCounts(countsForListedGuests(counts, guests));
      } catch (err) {
        console.error('Error reloading unread counts:', err);
      }
    };

    const unsubscribeNoteReads = onSnapshot(noteReadsQuery, reloadCounts);
    const unsubscribeCheckIns = onSnapshot(checkInsQuery, reloadCounts);
    const unsubscribeCheckOutNotes = onSnapshot(checkOutNotesQuery, reloadCounts);

    return () => {
      unsubscribeNoteReads();
      unsubscribeCheckIns();
      unsubscribeCheckOutNotes();
    };
  }, [currentUser, guests]);

  const filterGuests = () => {
    let filtered = [...guests];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(guest =>
        `${guest.firstName} ${guest.lastName}`.toLowerCase().includes(term) ||
        guest.email?.toLowerCase().includes(term) ||
        guest.phone?.includes(term)
      );
    }

    // Apply status filter
    if (filterStatus === 'complete') {
      filtered = filtered.filter(guest => guest.completeness === 100);
    } else if (filterStatus === 'incomplete') {
      filtered = filtered.filter(guest => guest.completeness < 100);
    }

    // Apply unread notes filter (but keep viewed rows visible during this session)
    if (showUnreadOnly) {
      filtered = filtered.filter(guest =>
        unreadNotesCounts[guest.id] > 0 || viewedInSession.has(guest.id)
      );
    }

    // Sort by last name
    filtered.sort((a, b) =>
      `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
    );

    setFilteredGuests(filtered);
  };

  const toggleRow = async (guestId, shouldScroll = false) => {
    const newExpanded = new Set(expandedRows);

    // When opening a new row, close all other rows and remove them from viewedInSession if read
    if (!newExpanded.has(guestId)) {
      // Close all other expanded rows
      const currentlyExpanded = Array.from(newExpanded);
      currentlyExpanded.forEach(id => {
        if (id !== guestId) {
          newExpanded.delete(id);
          // Remove from viewedInSession if no unread notes
          if (unreadNotesCounts[id] === 0 || !unreadNotesCounts[id]) {
            console.log(`Removing guest ${id} from viewedInSession (auto-collapse), unread count: ${unreadNotesCounts[id]}`);
            setViewedInSession(prev => {
              const updated = new Set(prev);
              updated.delete(id);
              return updated;
            });
          }
        }
      });

      newExpanded.add(guestId);

      // Mark as viewed in this session (to keep visible when filtering by unread)
      setViewedInSession(prev => new Set(prev).add(guestId));

      // Load notes history when expanding
      if (currentUser) {
        try {
          console.log('Loading notes history for guest:', guestId);
          const notesHistory = await getGuestCheckInHistory(guestId, currentUser.uid);
          console.log('Notes history loaded:', notesHistory);
          setGuestNotes(prev => ({ ...prev, [guestId]: notesHistory }));

          // Mark all notes as read (subscription will auto-update counts)
          console.log('Marking all guest notes as read');
          await markAllGuestNotesAsRead(currentUser.uid, guestId, currentUser.displayName || currentUser.email);
          console.log('Notes marked as read, counts will update via subscription');
        } catch (err) {
          console.error('Error loading notes:', err);
        }
      }

      // Auto-scroll to the expanded row if requested
      if (shouldScroll) {
        setTimeout(() => {
          const rowElement = document.getElementById(`guest-row-${guestId}`);
          if (rowElement) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100); // Small delay to ensure expansion happens first
      }
    } else {
      // User is manually collapsing this row
      console.log(`User manually collapsing guest ${guestId}, unread count: ${unreadNotesCounts[guestId]}`);
      newExpanded.delete(guestId);

      // Always remove from viewedInSession when manually collapsing
      // This will cause it to disappear if it has no unread notes
      console.log(`Removing guest ${guestId} from viewedInSession (manual collapse)`);
      setViewedInSession(prev => {
        const updated = new Set(prev);
        updated.delete(guestId);
        console.log(`viewedInSession after removing ${guestId}:`, Array.from(updated));
        return updated;
      });
    }
    setExpandedRows(newExpanded);
  };

  const handleAddNote = (guest) => {
    setSelectedGuestForNote(guest);
    setShowNoteModal(true);
  };

  const handleCloseNoteModal = () => {
    setShowNoteModal(false);
    setSelectedGuestForNote(null);
  };

  const handleSaveNote = async () => {
    // Real-time listener will automatically update the local state
    // when Firestore is updated, so we just close the modal
    handleCloseNoteModal();
  };

  // Edit handlers
  const handleEditGuest = (guest) => {
    setSelectedGuestForEdit(guest);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedGuestForEdit(null);
  };

  const handleSaveEdit = async () => {
    handleCloseEditModal();
  };

  // Open the file picker for the given guest. The actual upload happens
  // in handleFileSelected once the user has chosen a file.
  const handleAddOrReplacePhoto = (guest) => {
    if (!canEditGuests) return;
    setPendingPhotoGuest(guest);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // allow re-selecting the same file
      fileInputRef.current.click();
    }
  };

  // Resize the selected file, upload to Firebase Storage as the
  // participant's profile image (imageSource: 'app'), then for CCB-linked
  // guests immediately push the same image to CCB so both sides match.
  const handleFileSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    const guest = pendingPhotoGuest;
    setPendingPhotoGuest(null);
    if (!file || !guest) return;

    // Auto-expand row to show progress
    if (!expandedRows.has(guest.id)) {
      toggleRow(guest.id, true);
    }
    setUploadingPhotoFor(guest.id);
    setPhotoUploadMessage(null);
    setUploadProgress(0);
    setUploadStage('Resizing photo...');

    try {
      const resized = await resizeImage(file, 800, 800, 0.85);

      setUploadStage('Uploading photo...');
      setUploadProgress(35);
      const photoURL = await uploadGuestPhoto(guest.id, resized);
      // uploadGuestPhoto's Cloud Function already sets image + imageSource:'app'
      // on the participant doc, so we don't need to write Firestore again here.

      const isCCB = guest.id.startsWith('ccb_');
      if (isCCB) {
        setUploadStage('Syncing to CCB...');
        setUploadProgress(70);
        try {
          const numericId = guest.id.replace('ccb_', '');
          await uploadCCBProfileImageV2({ individualId: numericId, imageUrl: photoURL });
          await updateParticipant(guest.id, { ccbImageUploaded: true });
        } catch (ccbErr) {
          console.error('CCB image push failed (kept locally):', ccbErr);
          // Keep the local image; surface a softer message
          setPhotoUploadMessage({
            guestId: guest.id,
            type: 'warning',
            text: 'Photo saved locally; CCB push failed (will retry on next sync).'
          });
          await updateParticipant(guest.id, { ccbImageUploaded: false });
          setTimeout(() => setPhotoUploadMessage(null), 5000);
          return;
        }
      }

      setUploadProgress(100);
      setUploadStage('Complete!');
      setPhotoUploadMessage({
        guestId: guest.id,
        type: 'success',
        text: isCCB ? 'Photo uploaded and synced to CCB!' : 'Photo uploaded!'
      });
      setTimeout(() => setPhotoUploadMessage(null), 3000);
    } catch (error) {
      console.error('Photo upload error:', error);
      setPhotoUploadMessage({
        guestId: guest.id,
        type: 'error',
        text: `Upload failed: ${error.message || 'Unknown error'}`
      });
    } finally {
      setUploadingPhotoFor(null);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  const handleUploadPhotoToCCB = async (guest) => {
    if (!guest.image || !guest.id.startsWith('ccb_')) return;

    // Auto-expand the row to show progress
    if (!expandedRows.has(guest.id)) {
      toggleRow(guest.id, true);
    }

    setUploadingPhotoFor(guest.id);
    setPhotoUploadMessage(null);
    setUploadProgress(0);
    setUploadStage('Uploading to CCB...');

    // Animate progress — server fetches image directly from Firebase Storage URL
    let progressInterval = null;
    const startProgressAnimation = () => {
      let current = 0;
      progressInterval = setInterval(() => {
        current += 1;
        if (current >= 90) {
          clearInterval(progressInterval);
          progressInterval = null;
        }
        setUploadProgress(current);
      }, 150);
    };

    try {
      const numericId = guest.id.replace('ccb_', '');

      // Send just the URL — Cloud Function fetches the image server-side (fast)
      startProgressAnimation();
      await uploadCCBProfileImageV2({
        individualId: numericId,
        imageUrl: guest.image
      });

      // Mark as uploaded in Firestore so the indicator disappears
      await updateParticipant(guest.id, { ccbImageUploaded: true });

      if (progressInterval) clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadStage('Complete!');
      setPhotoUploadMessage({ guestId: guest.id, type: 'success', text: 'Photo uploaded to CCB!' });
      setTimeout(() => setPhotoUploadMessage(null), 3000);
    } catch (error) {
      console.error('Error uploading photo to CCB:', error);
      if (progressInterval) clearInterval(progressInterval);
      setPhotoUploadMessage({ guestId: guest.id, type: 'error', text: `Upload failed: ${error.message}` });
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      setUploadingPhotoFor(null);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  const isSysAdmin = userRole === 'sys_admin';
  // Read-only while viewing as someone: this also switches off the photo controls.
  const canEditGuests = !ro.disabled && (userRole === 'sys_admin' || userRole === 'aar_leader' || userRole === 'bt_admin');

  const exportToCSV = () => {
    if (filteredGuests.length === 0) return;

    const headers = [
      'First Name', 'Last Name', 'Gender', 'Birthday',
      'Email', 'Phone', 'Mobile Phone', 'Home Phone',
      'Street', 'City', 'State', 'Zip',
      'Emergency Contact', 'Emergency Phone',
      'Completeness %', 'Missing Fields'
    ];

    const rows = filteredGuests.map(guest => [
      guest.firstName || '',
      guest.lastName || '',
      guest.gender || '',
      guest.birthday || '',
      guest.email || '',
      guest.phone || '',
      guest.mobilePhone || '',
      guest.homePhone || '',
      guest.street || '',
      guest.city || '',
      guest.state || '',
      guest.zip || '',
      guest.emergencyContactName || '',
      guest.emergencyContactPhone || '',
      guest.completeness || 0,
      guest.missingFields?.join('; ') || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guest-profiles-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getCompletenessColor = (percentage) => {
    if (percentage >= 90) return '#10b981';
    if (percentage >= 70) return '#f59e0b';
    return '#ef4444';
  };

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredGuests.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredGuests.length / recordsPerPage);

  const scrollToTable = () => {
    const tableSection = document.querySelector('.profiles-table-section');
    if (tableSection) {
      tableSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setExpandedRows(new Set());
    scrollToTable();
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setExpandedRows(new Set());
      scrollToTable();
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setExpandedRows(new Set());
      scrollToTable();
    }
  };

  const renderFieldValue = (value, fieldName) => {
    if (value && value.toString().trim() !== '') {
      return (
        <div className="field-present">
          <span className="field-icon">✓</span>
          <span className="field-label-prefix">{fieldName}:</span>
          <span className="field-value">{value}</span>
        </div>
      );
    }
    return (
      <div className="field-missing">
        <span className="field-icon">✗</span>
        <span className="field-label">{fieldName}:</span>
        <span className="field-missing-text">(missing)</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading guest profiles...</p></div>
    );
  }

  if (error) {
    return (
      <div className="guest-profiles-container">
        <div className="error-message">
          <h3>Error Loading Profiles</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-profiles-container">
      {/* Hidden file input — triggered by the camera badge / "Add Photo"
          button to upload a profile picture for any CCB or non-CCB guest. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFileSelected}
      />

      {/* Header with Title and Back Button */}
      <div className="guest-profiles-header">
        <button className="btn-back" onClick={() => navigate('/check-in')}>
          ← Guest Check In
        </button>
        <h2>Guest Profiles</h2>
      </div>

      {/* Consolidated Controls Bar */}
      <div className="controls-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => { shouldResetPage.current = true; setSearchTerm(e.target.value); }}
            className="search-input"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => { shouldResetPage.current = true; setSearchTerm(''); setFilterStatus(e.target.value); }}
          className="filter-select"
        >
          <option value="all">All Guests ({guests.length})</option>
          <option value="complete">Complete Profiles ({guests.filter(g => g.completeness === 100).length})</option>
          <option value="incomplete">Incomplete Profiles ({guests.filter(g => g.completeness < 100).length})</option>
        </select>

        <button
          className="btn-bell"
          onClick={() => {
            if (!showUnreadOnly) {
              shouldResetPage.current = true;
              setShowUnreadOnly(true);
              navigate('/guest-profiles?unread=true');
            }
          }}
          title={Object.keys(unreadNotesCounts).length > 0 ? `${Object.keys(unreadNotesCounts).length} guest${Object.keys(unreadNotesCounts).length > 1 ? 's' : ''} with unread notes` : 'No unread notes'}
        >
          🔔
          {Object.keys(unreadNotesCounts).length > 0 && (
            <span className="bell-count">{Object.keys(unreadNotesCounts).length}</span>
          )}
        </button>

        {showUnreadOnly && (
          <button
            className="btn btn-secondary"
            onClick={() => {
              shouldResetPage.current = true;
              setShowUnreadOnly(false);
              navigate('/guest-profiles');
            }}
          >
            Show All Guests
          </button>
        )}

        <button className="btn btn-primary" onClick={exportToCSV}>
          Export to CSV
        </button>
      </div>

      {/* Guest Profiles Table */}
      <div className="profiles-table-section">
        {/* Top Pagination Controls */}
        {filteredGuests.length > recordsPerPage && (
          <div className="pagination-container top">
            <div className="pagination-info">
              Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredGuests.length)} of {filteredGuests.length} guests
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
              >
                « Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                // Show first page, last page, current page, and pages around current
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                      onClick={() => paginate(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}

              <button
                className="pagination-btn"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next »
              </button>
            </div>
          </div>
        )}

        {filteredGuests.length === 0 ? (
          <div className="empty-state">
            <p>No guests found matching your criteria.</p>
          </div>
        ) : (
          <table className="profiles-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Completeness</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
                <th className="expand-column"></th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.map((guest) => {
                const isExpanded = expandedRows.has(guest.id);
                return (
                  <React.Fragment key={guest.id}>
                    {/* Main Row */}
                    <tr id={`guest-row-${guest.id}`} className={`guest-row ${isExpanded ? 'expanded' : ''}`}>
                      <td className="guest-name">
                        <div className="name-cell">
                          <div className={`guest-avatar${needsPhotoUpload(guest) ? ' needs-ccb-upload' : ''}`}>
                            {hasRealImage(guest) ? (
                              <img
                                src={guest.image}
                                alt={`${guest.firstName} ${guest.lastName}`}
                                onLoad={(e) => handleAvatarLoad(guest.id, e)}
                                onError={() => handleAvatarError(guest.id)}
                              />
                            ) : (
                              <>
                                {guest.image && !failedImages.has(guest.id) && (
                                  <img
                                    src={guest.image}
                                    alt=""
                                    onLoad={(e) => handleAvatarLoad(guest.id, e)}
                                    onError={() => handleAvatarError(guest.id)}
                                    style={{ display: 'none' }}
                                  />
                                )}
                                <img src={placeholderAvatar} alt={`${guest.firstName} ${guest.lastName}`} className="avatar-placeholder-img" />
                              </>
                            )}
                            {needsPhotoUpload(guest) && (
                              <span
                                className="ccb-photo-badge"
                                title={
                                  hasRealImage(guest)
                                    ? "Photo not yet uploaded to CCB — click to push to CCB"
                                    : "No photo — click to add one"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!canEditGuests) return;
                                  if (hasRealImage(guest)) {
                                    handleUploadPhotoToCCB(guest);
                                  } else {
                                    handleAddOrReplacePhoto(guest);
                                  }
                                }}
                              >
                                📷
                              </span>
                            )}
                          </div>
                          <div className="name-text">
                            {guest.firstName} {guest.lastName}
                            {guest.id?.startsWith('ccb_') && (
                              <a
                                className="ccb-id-link"
                                href={`https://mybt.ccbchurch.com/goto/individuals/${guest.id.replace('ccb_', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="Open profile in CCB"
                              >
                                CCB ID: {guest.id.replace('ccb_', '')}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-info">
                          <div>{guest.email || 'No email'}</div>
                          <div>{guest.phone || 'No phone'}</div>
                        </div>
                      </td>
                      <td>
                        <div className="completeness-cell">
                          <span
                            className="completeness-percentage"
                            style={{ color: getCompletenessColor(guest.completeness) }}
                          >
                            {guest.completeness}%
                          </span>
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar-fill"
                              style={{
                                width: `${guest.completeness}%`,
                                backgroundColor: getCompletenessColor(guest.completeness)
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          {guest.criticalMissing && (
                            <span className="status-badge critical">Critical Missing</span>
                          )}
                          {guest.emergencyContactMissing && (
                            <span className="status-badge emergency">No Emergency Contact</span>
                          )}
                          {!guest.criticalMissing && !guest.emergencyContactMissing && guest.completeness === 100 && (
                            <span className="status-badge complete">Complete</span>
                          )}
                        </div>
                      </td>
                      <td className="actions-column">
                        <div className="action-buttons">
                          <button
                            className="btn-add-note"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddNote(guest);
                            }}
                            disabled={ro.disabled}
                            aria-label="Add note"
                            title={ro.title || 'Add note to guest profile'}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                          </button>
                          <button
                            className="btn-upload-ccb"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (hasRealImage(guest)) {
                                handleUploadPhotoToCCB(guest);
                              } else {
                                handleAddOrReplacePhoto(guest);
                              }
                            }}
                            disabled={uploadingPhotoFor === guest.id || !canEditGuests}
                            aria-label={hasRealImage(guest) ? 'Upload photo to CCB' : 'Add photo'}
                            title={ro.title || (hasRealImage(guest) ? 'Upload existing photo to CCB' : 'Add a photo for this guest')}
                          >
                            {uploadingPhotoFor === guest.id ? '...' : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
                          </button>
                          <button
                            className="btn-edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditGuest(guest);
                            }}
                            disabled={ro.disabled}
                            aria-label="Edit profile"
                            title={ro.title || 'Edit guest profile'}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </div>
                      </td>
                      <td className="expand-column">
                        <button
                          className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleRow(guest.id)}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          ▼
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {isExpanded && (
                      <tr className="details-row">
                        <td colSpan="6">
                          <div className="details-content">
                            <div className="details-grid">
                              {/* Personal Information */}
                              <div className="detail-section">
                                <h4>Personal Information</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(guest.firstName, 'First')}
                                  {renderFieldValue(guest.lastName, 'Last')}
                                  {renderFieldValue(guest.gender === 'm' ? 'Male' : guest.gender === 'f' ? 'Female' : guest.gender, 'Gender')}
                                  {renderFieldValue(guest.birthday, 'DOB')}
                                  {renderFieldValue(hasRealImage(guest) ? 'Yes' : '', 'Photo')}
                                </div>
                              </div>

                              {/* Contact Information */}
                              <div className="detail-section">
                                <h4>Contact Information</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(guest.email, 'Email')}
                                  {renderFieldValue(guest.phone, 'Phone')}
                                  {renderFieldValue(guest.mobilePhone, 'Mobile')}
                                  {renderFieldValue(guest.homePhone, 'Home')}
                                </div>
                              </div>

                              {/* Address Information */}
                              <div className="detail-section">
                                <h4>Address Information</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(guest.street, 'Street')}
                                  {renderFieldValue(guest.city, 'City')}
                                  {renderFieldValue(guest.state, 'State')}
                                  {renderFieldValue(guest.zip, 'Zip')}
                                </div>
                              </div>

                              {/* Emergency Contact */}
                              <div className="detail-section">
                                <h4>Emergency Contact</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(guest.emergencyContactName, 'Name')}
                                  {renderFieldValue(guest.emergencyContactPhone, 'Phone')}
                                </div>
                              </div>
                            </div>

                            {/* Photo Upload Progress */}
                            {uploadingPhotoFor === guest.id && (
                              <div className="photo-upload-progress">
                                <div className="upload-progress-header">
                                  <span className="upload-stage">{uploadStage}</span>
                                  <span className="upload-pct">{uploadProgress}%</span>
                                </div>
                                <div className="upload-progress-bar-container">
                                  <div
                                    className="upload-progress-bar-fill"
                                    style={{ width: `${uploadProgress}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Photo Upload Status */}
                            {photoUploadMessage && photoUploadMessage.guestId === guest.id && (
                              <div className={`photo-upload-message ${photoUploadMessage.type}`}>
                                {photoUploadMessage.type === 'success' ? '✓' : '✗'} {photoUploadMessage.text}
                              </div>
                            )}

                            {/* Notes History Section */}
                            {guestNotes[guest.id] && guestNotes[guest.id].length > 0 && (
                              <div className="notes-history-section">
                                <h4>Notes History</h4>
                                <div className="notes-timeline">
                                  {guestNotes[guest.id].map((note) => {
                                    const authorName = note.authorName || note.checkInByName || 'Unknown';
                                    const noteText = note.noteText || note.checkInNotes;
                                    const timestamp = note.timestamp || note.checkInTime;
                                    const source = note.source || (note.serviceType === 'note' ? 'Profile Note' : 'Check-In Note');
                                    return (
                                      <div key={note.id} className="note-entry-compact">
                                        <span className="note-author-avatar-compact">
                                          {authorName.charAt(0) || '?'}
                                        </span>
                                        <span className="note-author-name-compact">{authorName}</span>
                                        <span className={`note-source-badge note-source-${source === 'Check-In Note' ? 'checkin' : source === 'Check-Out Note' ? 'checkout' : 'profile'}`}>
                                          {source}
                                        </span>
                                        <span className="note-content-compact">{noteText}</span>
                                        <span className="note-separator">•</span>
                                        <span className="note-timestamp-compact">
                                          {timestamp?.toDate ? new Date(timestamp.toDate()).toLocaleString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                          }) : 'Unknown date'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {filteredGuests.length > recordsPerPage && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredGuests.length)} of {filteredGuests.length} guests
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
              >
                « Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => {
                // Show first page, last page, current page, and pages around current
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                      onClick={() => paginate(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}

              <button
                className="pagination-btn"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Note Modal */}
      {showNoteModal && selectedGuestForNote && (
        <AddNoteModal
          guest={selectedGuestForNote}
          onClose={handleCloseNoteModal}
          onSave={handleSaveNote}
        />
      )}

      {/* Edit Guest Modal */}
      {showEditModal && selectedGuestForEdit && (
        <EditGuestModal
          guest={selectedGuestForEdit}
          onClose={handleCloseEditModal}
          onSave={handleSaveEdit}
        />
      )}

      {/* Delete Confirmation Modal */}
    </div>
  );
}

export default GuestProfiles;
